/**
 * Client for the local-only developer access controller (#567).
 *
 * The controller is a SEPARATE local-overlay deployment, not an Editor Service
 * route, so it is deliberately not part of the generated contract client: its
 * base URL exists only in the local runtime configuration (`config.local`),
 * the schema refuses that block outside the `local` environment, and cloud
 * bundles therefore carry no reachable path to this code's endpoints.
 *
 * The request the controller accepts has NO subject field — it derives the
 * subject from the same validated Entra token the Editor validates — so this
 * client can only ever change the signed-in developer's own local roles.
 */

import type { AuthAdapter } from "@/auth/types";
import type { RuntimeConfig } from "@/config/schema";

/** The assignable role vocabulary, as the authorization contract spells it. */
export const LOCAL_ROLES = [
  "admin",
  "project-editor",
  "reviewer-sign-off",
  "library-author",
] as const;

export type LocalRole = (typeof LOCAL_ROLES)[number];

export interface LocalProfile {
  subject: string;
  displayName?: string;
  roles: LocalRole[];
}

export interface LocalAccessConfig {
  baseUrl: string;
}

/**
 * The named profiles the banner offers. `roles: []` IS the no-access profile;
 * an explicit combination is chosen through the multi-select. Admin first: it
 * is the browser-local default the issue specifies.
 */
export const NAMED_PROFILES: ReadonlyArray<{ label: string; roles: LocalRole[] }> = [
  { label: "Admin", roles: ["admin"] },
  { label: "Project editor", roles: ["project-editor"] },
  { label: "Clinical Manager", roles: ["reviewer-sign-off"] },
  { label: "Library author", roles: ["library-author"] },
  { label: "No access", roles: [] },
];

/**
 * The controller's coordinates, or null when role simulation is off.
 *
 * Null in every cloud environment BY SCHEMA (`config.local` is refused outside
 * `local`), null in the strict local lane (the block is absent), and null when
 * the block exists with `roleSimulation: false`. The double condition on the
 * environment is defense in depth over the schema rule, not a second policy.
 */
export function localAccessConfig(config: RuntimeConfig): LocalAccessConfig | null {
  if (config.environment !== "local") return null;
  if (!config.local || !config.local.roleSimulation) return null;
  return { baseUrl: config.local.accessControllerBaseUrl.replace(/\/+$/, "") };
}

export class LocalAccessError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = "LocalAccessError";
    this.status = status;
  }
}

async function request(
  access: LocalAccessConfig,
  adapter: AuthAdapter,
  method: "GET" | "PUT",
  body?: unknown,
): Promise<LocalProfile> {
  const token = await adapter.getAccessToken();
  if (!token) {
    throw new LocalAccessError("No access token is available for the local controller.", null);
  }
  let response: Response;
  try {
    response = await fetch(`${access.baseUrl}/local/v1/self-profile`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new LocalAccessError("The local access controller could not be reached.", null);
  }
  if (!response.ok) {
    throw new LocalAccessError(
      `The local access controller refused the request (HTTP ${response.status}).`,
      response.status,
    );
  }
  const parsed: unknown = await response.json().catch(() => null);
  const candidate = parsed as { subject?: unknown; displayName?: unknown; roles?: unknown } | null;
  if (
    !candidate ||
    typeof candidate.subject !== "string" ||
    !Array.isArray(candidate.roles) ||
    !candidate.roles.every(
      (role): role is LocalRole => typeof role === "string" && (LOCAL_ROLES as readonly string[]).includes(role),
    )
  ) {
    throw new LocalAccessError("The local access controller answered an unexpected shape.", response.status);
  }
  return {
    subject: candidate.subject,
    ...(typeof candidate.displayName === "string" ? { displayName: candidate.displayName } : {}),
    roles: [...candidate.roles],
  };
}

export function fetchSelfProfile(access: LocalAccessConfig, adapter: AuthAdapter): Promise<LocalProfile> {
  return request(access, adapter, "GET");
}

export function putSelfProfile(
  access: LocalAccessConfig,
  adapter: AuthAdapter,
  roles: LocalRole[],
): Promise<LocalProfile> {
  return request(access, adapter, "PUT", { roles });
}

/**
 * The developer's chosen profile, remembered per browser. `admin` when nothing
 * is stored — the browser-local default #567 rules — and validated on read so
 * a stale or hand-edited entry degrades to that default instead of sending an
 * invalid profile the controller would 422.
 */
const STORAGE_KEY = "editor-local-desired-roles";

export function storedDesiredRoles(): LocalRole[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return ["admin"];
    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (role): role is LocalRole => typeof role === "string" && (LOCAL_ROLES as readonly string[]).includes(role),
      )
    ) {
      return [...new Set(parsed)];
    }
  } catch {
    // Unreadable storage or malformed JSON — fall through to the default.
  }
  return ["admin"];
}

export function storeDesiredRoles(roles: LocalRole[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
  } catch {
    // Storage may be unavailable (private mode); the profile still applied
    // server-side, so losing the preference is the acceptable failure.
  }
}
